import type { CyanGoal, GoalRisk } from "./types";

export interface RawOwnerGoalInput {
  text: string;
  projectId?: string | null;
  conversationId?: string | null;
}

export interface GoalUnderstandingResult {
  normalizedIntent: string;
  desiredOutcome: string;
  constraints: string[];
  successCriteria: string[];
  assumptions: string[];
  ambiguities: string[];
  inferredRisk: GoalRisk;
  requiresClarification: boolean;
}

function normalizeWhitespace(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

function extractSentences(value: string): string[] {
  return value
    .split(/[.!?]+/)
    .map(normalizeWhitespace)
    .filter(Boolean);
}

function inferRisk(normalized: string): GoalRisk {
  const lower = normalized.toLowerCase();

  if (
    /\b(delete production|drop database|rotate credentials|transfer ownership|payment|billing|legal|identity|production deploy)\b/.test(
      lower,
    )
  ) {
    return "critical";
  }

  if (
    /\b(delete|deploy|database|migration|credential|permission|production|security)\b/.test(
      lower,
    )
  ) {
    return "high";
  }

  if (
    /\b(change|modify|update|refactor|install|create|build|fix)\b/.test(lower)
  ) {
    return "medium";
  }

  return "low";
}

function detectAmbiguities(normalized: string): string[] {
  const ambiguities: string[] = [];

  if (!normalized) {
    ambiguities.push("Owner goal is empty.");

    return ambiguities;
  }

  if (normalized.length < 8) {
    ambiguities.push("Goal is too short to determine reliable intent.");
  }

  if (
    /\b(make it better|fix it|improve it|do it|handle it)\b/i.test(normalized)
  ) {
    ambiguities.push("Goal contains an underspecified reference or outcome.");
  }

  return ambiguities;
}

export class CyanGoalUnderstandingEngine {
  understand(input: RawOwnerGoalInput): GoalUnderstandingResult {
    const normalizedIntent = normalizeWhitespace(input.text);

    const sentences = extractSentences(normalizedIntent);

    const ambiguities = detectAmbiguities(normalizedIntent);

    const desiredOutcome = sentences[0] ?? normalizedIntent;

    const constraints = sentences.filter((sentence) =>
      /\b(must|must not|never|without|only|do not|cannot|can't)\b/i.test(
        sentence,
      ),
    );

    const successCriteria = sentences.filter((sentence) =>
      /\b(done|complete|working|verified|pass|green|success|100%)\b/i.test(
        sentence,
      ),
    );

    return {
      normalizedIntent,
      desiredOutcome,
      constraints,
      successCriteria,
      assumptions: [],
      ambiguities,
      inferredRisk: inferRisk(normalizedIntent),

      requiresClarification: ambiguities.length > 0,
    };
  }

  createGoal(id: string, understanding: GoalUnderstandingResult): CyanGoal {
    return {
      id,
      ownerIntent: understanding.normalizedIntent,

      desiredOutcome: understanding.desiredOutcome,

      constraints: understanding.constraints,

      successCriteria: understanding.successCriteria,

      status: understanding.requiresClarification
        ? "understanding"
        : "planning",
    };
  }
}
